import { useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckIcon, CopyIcon } from 'lucide-react'

import { Button } from './ui/button'
import { Input } from './ui/input'

import { cn } from '@/lib/utils'

interface CopyToClipboardProps {
  value: string
  placeholder?: string
  className?: string
  buttonVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  buttonClassName?: string
  inputClassName?: string
  disabled?: boolean
  onCopy?: (value: string) => void
  readOnly?: boolean
  showInput?: boolean
  autoResetDelay?: number
}

export function CopyToClipboard({
  value,
  placeholder,
  className,
  buttonVariant = 'outline',
  buttonClassName,
  inputClassName,
  disabled = false,
  onCopy,
  readOnly = true,
  showInput = true,
  autoResetDelay = 3000,
}: CopyToClipboardProps) {
  const [isCopied, setIsCopied] = useState(false)
  const [isDisabled, setIsDisabled] = useState(false)

  const handleCopy = async () => {
    if (disabled || isDisabled) return

    try {
      await navigator.clipboard.writeText(value)
      setIsCopied(true)
      setIsDisabled(true)
      onCopy?.(value)

      setTimeout(() => {
        setIsCopied(false)
        setIsDisabled(false)
      }, autoResetDelay)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  return (
    <div className={cn('flex flex-row items-center gap-2', className)}>
      {showInput && (
        <Input value={value} placeholder={placeholder} readOnly={readOnly} className={cn('flex-1', inputClassName)} />
      )}

      <motion.div whileTap={{ scale: 0.95 }}>
        <Button
          onClick={handleCopy}
          disabled={disabled || isDisabled}
          className={cn('w-10 h-10 p-2 relative overflow-hidden', buttonClassName)}
          variant={buttonVariant}
        >
          <AnimatePresence mode="wait">
            {isCopied ? (
              <motion.div
                key="check"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: 180 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 25,
                }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <CheckIcon className="w-5 h-5 text-primary" />
              </motion.div>
            ) : (
              <motion.div
                key="copy"
                initial={{ scale: 0, rotate: 180 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0, rotate: -180 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 25,
                }}
                className="absolute inset-0 flex items-center justify-center"
              >
                <CopyIcon className="w-4 h-4" />
              </motion.div>
            )}
          </AnimatePresence>
        </Button>
      </motion.div>
    </div>
  )
}
